import { Grid, Box, Typography, Popper, Fade, Paper, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import React, { useEffect, useMemo, useState } from 'react';
import ProductDescription from './ProductDescription';
import ShipmentTypeDetails from './ShipmentTypeDetails';
import BoxList from './BoxList';
import TotalValues from './TotalValue';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import Actions from '../Actions';
import { getRates, setCreateShipmentCSP } from '../../../store/reducers/GetRates';
import { useDispatch, useSelector } from 'react-redux';
import { SHIPMENT_TYPES } from '../../GetRatesV2/Constant';
import { AddressType } from '../../../store/reducers/GetRates/Type';
import { AppDispatch, RootStateType } from '../../../store';
import NoCSPFound from '../../GetRatesV2/CSPList/NoCSPFound/NoCSPFound';
import ViewAction from '../../ViewSingleShipment/ViewAction';
import { useAiValidation } from '../../../hooks/useAiValidation';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';

function getAddressObj({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

function PackageAiValidationAlert({
  validationState,
}: {
  validationState?: {
    loading?: boolean;
    valid?: boolean | null;
    suggestions?: string[];
    error?: string | null;
  };
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const loading = validationState?.loading;
  const valid = validationState?.valid;
  const suggestions = validationState?.suggestions || [];

  // If valid === true or no validation has run yet, show nothing
  if (!loading && (valid === true || valid === null || valid === undefined)) {
    return null;
  }

  const isOpen = (Boolean(anchorEl) || isHovered) && (loading === true || valid === false);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
      {loading && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#7c4dff',
            px: 1.5,
            py: 0.5,
            borderRadius: '20px',
            background: 'rgba(124, 77, 255, 0.08)',
            border: '1px solid rgba(124, 77, 255, 0.2)',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '0.95rem', animation: 'sparkleRotate 2s linear infinite' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>AI Analyzing Package...</Typography>
        </Box>
      )}

      {!loading && valid === false && (
        <>
          <Box
            onMouseEnter={e => {
              setAnchorEl(e.currentTarget);
              setIsHovered(true);
            }}
            onMouseLeave={() => setIsHovered(false)}
            onClick={e => setAnchorEl(anchorEl ? null : e.currentTarget)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: theme.palette.error.main,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              background: 'rgba(211, 47, 47, 0.06)',
              cursor: 'pointer',
              animation: 'fadeInScale 0.3s ease',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgba(211, 47, 47, 0.12)',
              },
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
              AI Alert{suggestions.length > 1 ? ` (${suggestions.length} issues)` : ''}
            </Typography>
          </Box>

          <Popper
            open={isOpen}
            anchorEl={anchorEl}
            placement="bottom-start"
            transition
            disablePortal={false}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
                options: { altAxis: true, padding: 8 },
              },
            ]}
            style={{ zIndex: 1300, maxWidth: 450 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={250}>
                <Paper
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => {
                    setIsHovered(false);
                    setAnchorEl(null);
                  }}
                  sx={{
                    mt: 1,
                    p: 2,
                    borderRadius: '16px',
                    boxShadow:
                      theme.palette.mode === 'dark'
                        ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                        : '0 8px 32px rgba(211, 47, 47, 0.1)',
                    border: theme.palette.mode === 'dark' ? '1px solid #7f1d1d' : '1px solid #fecaca',
                    background: theme.palette.mode === 'dark' ? '#241010' : '#fff5f5',
                    borderLeft: `4px solid ${theme.palette.error.main}`,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AutoAwesomeIcon sx={{ fontSize: '1rem', color: theme.palette.error.main }} />
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: theme.palette.error.main,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          AI Validation
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          px: '6px',
                          py: '2px',
                          borderRadius: '4px',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.error.main }}>
                          Attention Required
                        </Typography>
                      </Box>
                    </Box>

                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mt: 0.5 }}>
                      Package details need attention
                    </Typography>

                    {suggestions.length > 1 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, my: 0.5 }}>
                        {suggestions.map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: theme.palette.error.main, lineHeight: 1.4 }}>
                              •
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : suggestions.length === 1 ? (
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.4, my: 0.5 }}>
                        {suggestions[0]}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'text.primary', my: 0.5 }}>
                        This package couldn't be verified by AI.
                      </Typography>
                    )}

                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', opacity: 0.85, mt: 0.5 }}>
                      Please verify before continuing.
                    </Typography>
                  </Box>
                </Paper>
              </Fade>
            )}
          </Popper>
        </>
      )}
    </Box>
  );
}

function ShipmentDetails({
  removeShipmentInfo,
  showViewMoreAction,
  isShipmentDetailsWriteAllowed,
}: {
  removeShipmentInfo?: boolean;
  showViewMoreAction?: boolean;
  isShipmentDetailsWriteAllowed?: boolean;
}) {
  const [viewMore, setViewMore] = useState<boolean>(true);

  const dispatch = useDispatch<AppDispatch>();
  const {
    activeStep,
    setActiveStep,
    setShowRates,
    chargeableWeight,
    boxes,
    setSelectedBox,
    setSelectedProduct,
    consignee,
    shipper,
    totalShipmentValue: value,
    shipmentType,
    boxValidation,
    userId,
    pickupType: { selectedWearhouseAddress },
    pickupType,
    packageData,
    extraDetails,
    setDutyChargeError,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const { validationStates, validate } = useAiValidation();

  const rates = useSelector((state: RootStateType) => state.getRates);
  const { chareableWeight = { maxChargeableWt: 0 } } = useSelector((state: RootStateType) => state.getRates);
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const countriesNameObj = useSelector((state: RootStateType) => state.global.countriesNameObj) || {};
  const countries = useSelector((state: RootStateType) => state.global.countries) || [];

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '', _country } = organizationData || {};

  const orgCountryName = _country?.name?.trim() || '';

  const getCountryName = (field: any): string => {
    if (!field) return '';
    if (typeof field === 'string') return field.trim();
    if (typeof field === 'object') {
      if (typeof field.country === 'string') return field.country.trim();
      if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
        return field.country.name.trim();
      }
    }
    return '';
  };

  const shipperCountry = getCountryName(shipper);
  const consigneeCountry = getCountryName(consignee);

  let checkCountries: string[] = [];
  if (shipperCountry || consigneeCountry) {
    checkCountries = [shipperCountry, consigneeCountry].filter(Boolean);
  } else {
    checkCountries = [getCountryName(shipmentById?._shipperId), getCountryName(shipmentById?._consigneeId)].filter(
      Boolean,
    );
  }

  const isShipmentExport = orgCountryName
    ? checkCountries.some(c => c.toLowerCase() !== orgCountryName.toLowerCase())
    : false;

  console.log('chargeableWeight:', chargeableWeight);

  // Stable shipmentPackage validation payload builder
  const packagePayload = useMemo(() => {
    const purpose =
      extraDetails?.purposeOfShipment && String(extraDetails.purposeOfShipment).trim()
        ? String(extraDetails.purposeOfShipment).trim()
        : 'FOR_SALE';

    const insurance = typeof packageData?.insurance === 'boolean' ? packageData.insurance : false;
    const unitOfMeasurement = shipmentType?.unit || packageData?.unitOfMeasurement || 'kg-cm';
    const currency = shipmentType?.currency || packageData?.currency || 'INR';

    const boxPayload = (boxes || []).map(b => ({
      length: Number(b.length || 0),
      width: Number(b.width || 0),
      height: Number(b.height || 0),
      weight: Number(b.weight || 0),
      products: (b.products || []).map((p: any) => ({
        description: String(p.description || '').trim(),
        quantity: Number(p.quantity || 0),
        unitValue: Number(p.unitValue || 0),
        HSNName: p.HSNName ? String(p.HSNName).trim() : '',
        HSNCode: p.HSNCode ? String(p.HSNCode).trim() : '',
      })),
    }));

    return {
      purposeOfShipment: purpose,
      insurance,
      unitOfMeasurement,
      currency,
      box: boxPayload,
    };
  }, [
    extraDetails?.purposeOfShipment,
    packageData?.insurance,
    packageData?.unitOfMeasurement,
    packageData?.currency,
    shipmentType?.unit,
    shipmentType?.currency,
    boxes,
  ]);

  // Trigger centralized AI package validation
  useEffect(() => {
    validate('shipmentPackage', 'shipmentPackage', packagePayload);
  }, [packagePayload, validate]);

  useEffect(() => {
    if (rates.redirectToCreateShipmentCSP) {
      setActiveStep(activeStep + 1);
      dispatch(setCreateShipmentCSP(false));
    }
  }, [rates.redirectToCreateShipmentCSP]);

  function handleGetRates() {
    if (isDutyChargesAllowedSubdomain() && !getDutyChargeType(shipmentType.dutyCharge)) {
      setDutyChargeError('Duty Charge is required.');
      return;
    }

    if (isShipmentExport) {
      // Find the first product that is missing HSN Code
      let missingProduct: any = null;
      let missingBox: any = null;
      let missingProductIndex = -1;

      for (const box of boxes) {
        if (box.products) {
          const idx = box.products.findIndex(p => !p.HSNCode?.trim());
          if (idx !== -1) {
            missingProduct = box.products[idx];
            missingBox = box;
            missingProductIndex = idx;
            break;
          }
        }
      }

      if (missingProduct) {
        // 1. Select the box
        setSelectedBox(missingBox);
        // 2. Select the product to load it into the form for editing and show validation
        setSelectedProduct({
          ...missingProduct,
          id: missingProductIndex.toString(),
          isUpdate: true,
        });
        return; // Do not call the API
      }

      const getCountryCode = (countryName: string): string => {
        if (!countryName) return '';
        const trimmedName = countryName.trim();
        if (countriesNameObj[trimmedName]?.countryCode) {
          return countriesNameObj[trimmedName].countryCode;
        }
        const keys = Object.keys(countriesNameObj);
        const matchedKey = keys.find(k => k.toLowerCase() === trimmedName.toLowerCase());
        if (matchedKey && countriesNameObj[matchedKey]?.countryCode) {
          return countriesNameObj[matchedKey].countryCode;
        }
        const foundCountry = countries.find(c => {
          if (c.name?.toLowerCase() === trimmedName.toLowerCase()) return true;
          if (c.alternateNames?.some(alt => alt.toLowerCase() === trimmedName.toLowerCase())) return true;
          return false;
        });
        return foundCountry?.countryCode || '';
      };

      const sourceAddr = selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper;
      const destAddr = consignee;

      const internationalPayload = {
        destination: {
          city: destAddr.city || '',
          state: destAddr.state || '',
          country: destAddr.country || '',
          countryCode: getCountryCode(destAddr.country),
          pincode: destAddr.pincode || '',
        },
        source: {
          city: sourceAddr.city || '',
          state: sourceAddr.state || '',
          country: sourceAddr.country || '',
          countryCode: getCountryCode(sourceAddr.country),
          pincode: sourceAddr.pincode || '',
        },
        shipmentType: shipmentType.type,
        userId,
        weight: chareableWeight?.maxChargeableWt || 0,
        value,
        package: {
          box: boxes.flatMap(box => {
            const boxWeight = box.weight !== undefined && box.weight !== null ? box.weight.toString() : '0';
            const boxLength = box.length !== undefined && box.length !== null ? box.length.toString() : '0';
            const boxWidth = box.width !== undefined && box.width !== null ? box.width.toString() : '0';
            const boxHeight = box.height !== undefined && box.height !== null ? box.height.toString() : '0';

            return (box.products || []).map(product => ({
              hsnCode: product.HSNCode || '',
              qty: product.quantity || '',
              value: product.unitValue ?? 0,
              description: product.description || '',
              weight: boxWeight,
              length: boxLength,
              width: boxWidth,
              height: boxHeight,
            }));
          }),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        insurance: packageData.insurance,
        packaging: packageData.packaging,
        currency: shipmentType.currency,
        dropOff: pickupType.type === 'dropOff' ? true : false,
        organizationName: name === 'Cargo Solutions Network',
        rateFor: 'create-shipment' as const,
        ...(isDutyChargesAllowedSubdomain() && {
          dutyCharge: getDutyChargeType(shipmentType.dutyCharge),
        }),
      };

      dispatch(getRates([internationalPayload, false]));
    } else {
      // Domestic flow: keep completely unchanged
      const obj = {
        destination: { ...getAddressObj(consignee) },
        source: { ...getAddressObj(selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper) },
        shipmentType: shipmentType.type,
        userId,
        weight: chareableWeight?.maxChargeableWt || 0,
        value,
        package: {
          box: boxes.map(box => ({
            weight: box.weight.toString(),
            length: box.length,
            width: box.width,
            height: box.height,
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        insurance: packageData.insurance,
        packaging: packageData.packaging,
        dropOff: pickupType.type === 'dropOff' ? true : false,
        ...(isDutyChargesAllowedSubdomain() && {
          dutyCharge: getDutyChargeType(shipmentType.dutyCharge),
        }),
      };
      dispatch(
        getRates([{ ...obj, organizationName: name === 'Cargo Solutions Network', rateFor: 'create-shipment' }, false]),
      );
    }
  }

  return (
    <>
      <Grid container>
        {!removeShipmentInfo && <ShipmentTypeDetails />}
        <Grid container item alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <TotalValues />
          <PackageAiValidationAlert validationState={validationStates['shipmentPackage']} />
        </Grid>
        {showViewMoreAction && (
          <Grid container justifyContent="flex-end">
            <ViewAction setViewMore={setViewMore} viewMore={viewMore} />
          </Grid>
        )}
        {(viewMore || !showViewMoreAction) && (
          <Grid container className="my-3" spacing={2}>
            <Grid item lg={4} xl={3} md={4} sm={12} xs={12}>
              <BoxList writeAccess={isShipmentDetailsWriteAllowed} />
            </Grid>
            <Grid item lg={8} xl={9} md={8} sm={12} xs={12}>
              <ProductDescription writeAccess={isShipmentDetailsWriteAllowed} />
              {/* <DashedBtn classes="w-full" label={'Add a Product'} onClick={handleAddNewProduct} /> */}
            </Grid>
          </Grid>
        )}
      </Grid>
      {!removeShipmentInfo && <Actions onNext={handleGetRates} disableNext={!boxValidation} />}
      <NoCSPFound setShowRates={setShowRates} />
    </>
  );
}

export default ShipmentDetails;
